import React, { FC, useCallback } from 'react'
import {
  CwsSection,
  CwsA,
  CwsButton,
  CwsHeader,
  CwsHeaderLogo,
  CwsHeaderMegamenu,
  CwsHeaderMenuNavItem,
  CwsHeaderMenuNav,
  CwsHeaderShopping,
  CwsHeaderShoppingHeader,
  CwsHeaderShoppingList,
  CwsHeaderWrapper,
  CwsIcon,
  CwsHeaderMegamenuItem,
  CwsHeaderProfileNavigation,
  CwsHeaderProfileNavigationItem,
  CwsHeaderProfileContent,
  CwsHeaderNav,
  CwsHeaderLocaleButton,
  CwsHeaderProfile,
  CwsHeaderNavItem,
  CwsHeaderMenu,
} from '@components/cws'
import Link from 'next/link'
import { isGloballyPricelist } from '@utils/pricelist-utils/app-properties'
import { CartItem } from '@model/cart/CartItem'
import { useTranslate } from '@hooks/useTranslation'
import { getFormattedPrice } from '@utils/price'
import { Currency } from '@model/product/PriceInfo'
import ListItem from '@components/common/Header/CartListItem'
import { CurrenciesInfo } from '@hooks/useCurrency'
import PriceListItem from '@components/common/Header/PriceListItem'
import { WishlistItem } from '@model/wishlist/WishlistItem'
import { BundleInfo } from '@components/profile/Wishlist/Wishlist'
import { getSlug, SEPARATOR } from '@utils/slugs'
import { useRouter } from 'next/dist/client/router'
import { CategoryTip } from '@model/category/CategoryTip'
import { UpperNavigation } from '@components/common/Header/UpperNavigation'
import { HeaderSearch } from './HeaderSearch'
import { useAppState } from '@context/state'
import { HeaderMenuMobile } from '@components/common/Header/HeaderMenuMobile'
import { Locale } from '@model/locales'
import { LanguageSelector } from '@components/common/Header/LanguageSelector'
import { SSOContainer } from '@components/common/Header/SSOContainer'
import { CatalogFromContent } from '@api/content'
import { HeaderWishlist } from '@components/common/Header/HeaderWishlist'
import { usePreloadedInitialDataData } from '@context/PreloadedInitialDataContext'

interface IntegratedMobileHeaderProps {
  productListCount: number
  cart: CartItem[]
  itemsCount: number
  totalPriceCart: number
  currency: Currency
  currencies: CurrenciesInfo[]
  wishlist: WishlistItem[]
  shoppingListLink: string
  totalPriceWishlist: number
  setTotalCartPrices: (itemSku: string, price: number) => void
  setTotalWishlistPrices: (itemSku: string, price: number) => void
  openLegacyWishlist: () => void
  bundleGroupsWishList: Record<string, BundleInfo[]>
  bundleGroupsCart: Record<string, BundleInfo[]>
  openLegacyBasket: () => void
  categories: CategoryTip[]
  catalogs: CatalogFromContent[]
  freeProductsWishList: Record<string, number | string[]>
  freeProductsCart: Record<string, number | string[]>
}
export const IntegratedMobileHeader: FC<IntegratedMobileHeaderProps> = ({
  cart,
  itemsCount,
  totalPriceCart,
  currency,
  setTotalCartPrices,
  wishlist,
  shoppingListLink,
  totalPriceWishlist,
  setTotalWishlistPrices,
  openLegacyWishlist,
  bundleGroupsWishList,
  bundleGroupsCart,
  openLegacyBasket,
  categories,
  productListCount,
  catalogs,

  freeProductsWishList,
  freeProductsCart,
}) => {
  const { translate } = useTranslate()
  const { locale, query, asPath, push, pathname } = useRouter()
  const isCurrentCategory = useCallback(
    (categoryId: string) => {
      if (asPath.includes('category')) {
        const { slug } = query
        return typeof slug === 'string' && slug.endsWith(categoryId)
      }
      return false
    },
    [query, asPath]
  )
  const cartItems = cart.length > 3 ? cart.slice(0, 3) : cart
  const wishlistItems = wishlist.length > 3 ? wishlist.slice(0, 3) : wishlist

  const clubOneUrl = usePreloadedInitialDataData().topNavigation['Club One']

  const {
    state: { customer },
  } = useAppState()

  const handleOpenSso = (event: MouseEvent) => {
    event.stopPropagation()
    window.SSO.open()
  }

  return (
    <>
      <CwsHeaderWrapper mobile token={!!customer} locale={locale}>
        <SSOContainer isMobile locale={locale ?? ''} />
        <CwsHeader>
          <CwsHeaderLogo>
            <Link passHref href="/" legacyBehavior>
              <CwsA hideArrow theme="midnight">
                <CwsIcon
                  color="white"
                  height={isGloballyPricelist() ? '28' : '24'}
                  width={isGloballyPricelist() ? '80' : '100'}
                  icon={isGloballyPricelist() ? 'logo-tallink-silja-short' : 'logo-tallink-e-store-short'}
                />
              </CwsA>
            </Link>
          </CwsHeaderLogo>
          <CwsSection className={'cws-d-flex cws-p-8'} variant="div">
            <HeaderSearch />
            {Boolean(itemsCount) && (
              <CwsHeaderShopping icon="shopping" tagNotification>
                <CwsHeaderShoppingHeader
                  count={productListCount}
                  title={translate('cart.tile.shoppingcart')}
                  icon="shopping"
                ></CwsHeaderShoppingHeader>
                <CwsHeaderShoppingList
                  moreItemsLabel={cart.length > 3 ? translate('cart.more.items', { count: cart.length - 3 }) : ''}
                  price={getFormattedPrice(totalPriceCart, currency)}
                >
                  {cartItems?.map((item) => {
                    return item.product ? (
                      <ListItem
                        key={item.id}
                        item={item}
                        currency={currency}
                        isWishlist={false}
                        setTotalCartPrices={setTotalCartPrices}
                        bundleGroups={bundleGroupsCart}
                        quantityProductIsFree={(freeProductsCart[item.product.sku] as number) || 0}
                        isMobile
                      />
                    ) : null
                  })}
                </CwsHeaderShoppingList>
                <CwsButton className="cws-mt-xs" width="fluid" onClick={openLegacyBasket}>
                  {translate('basket.go.to.checkout')}
                </CwsButton>
              </CwsHeaderShopping>
            )}
            <CwsHeaderProfile>
              <CwsHeaderProfileNavigation>
                <CwsHeaderProfileNavigationItem
                  onClick={handleOpenSso}
                  mode="sso"
                  label={customer ? `${customer.lastName} ${customer.firstName}` : translate('w2.not.customer.text')}
                />

                {!!wishlist.length && (
                  <CwsHeaderProfileNavigationItem label={translate('wishlist.tile.wishlist')} mode="wishlist" />
                )}
              </CwsHeaderProfileNavigation>

              {!!wishlist.length && (
                <CwsHeaderProfileContent mode="wishlist">
                  <HeaderWishlist
                    {...{
                      freeProducts: freeProductsWishList,
                      wishlist,
                      totalPriceWishlist,
                      currency,
                      bundleGroups: bundleGroupsWishList,
                      setTotalWishlistPrices,
                      openLegacyWishlist,
                    }}
                  />
                </CwsHeaderProfileContent>
              )}
            </CwsHeaderProfile>
            <HeaderMenuMobile>
              {Boolean(categories.length) && (
                <CwsHeaderMenuNav>
                  {categories.map((item) => {
                    const icon = item.custom3
                    const slug = getSlug(`${item.name}`, item.id)
                    let href = query.catalogId
                      ? `/${locale}/${query.catalogId}/category/${slug}`
                      : `/${locale}/category/${slug}`

                    if (item.searchKey === 'clubone') {
                      href = query.catalogId
                        ? `/${locale}/${query.catalogId}/collection/${item.description}`
                        : `/${locale}/collection/${item.description}`
                    } else if (item.searchKey === 'brands') {
                      href = `/${locale}/${query.catalogId}/brands`
                    }
                    const isNotification = item?.custom2 === 'campaign'
                    return (
                      <CwsHeaderMenuNavItem
                        key={item.id}
                        current={isCurrentCategory(item.id + '')}
                        label={item.name}
                        href={href}
                        icon={icon}
                        tagNotification={isNotification}
                      >
                        <CwsA href={href} />
                        {item.children.length ? (
                          <CwsHeaderMegamenu>
                            {item.children
                              .filter((item) => !!item.children.length)
                              .map((subCategory) => {
                                const slug = getSlug(`${item.name}${SEPARATOR}${subCategory.name}`, subCategory.id)
                                if (!subCategory.children.length) return null

                                const href = {
                                  pathname: query.catalogId ? '/[catalogId]/category/[slug]' : '/category/[slug]',
                                  query: { catalogId: query.catalogId, slug },
                                }

                                return (
                                  <CwsHeaderMegamenuItem
                                    key={slug}
                                    // title={subCategory.name}
                                    title={
                                      <Link href={href} passHref>
                                        {subCategory.name}
                                      </Link>
                                    }
                                  >
                                    {subCategory.children
                                      .filter(({ numberOfProducts }) => numberOfProducts > 0)
                                      .map((cat) => {
                                        const slug = getSlug(
                                          `${item.name}${SEPARATOR}${subCategory.name}${SEPARATOR}${cat.name}`,
                                          cat.id
                                        )

                                        const href = {
                                          pathname: query.catalogId
                                            ? '/[catalogId]/category/[slug]'
                                            : '/category/[slug]',
                                          query: { catalogId: query.catalogId, slug },
                                        }

                                        /* This should have not worked before? */
                                        return (
                                          <Link href={href} passHref key={slug} legacyBehavior>
                                            <CwsA hideArrow>{cat.name}</CwsA>
                                          </Link>
                                        )
                                      })}
                                  </CwsHeaderMegamenuItem>
                                )
                              })}
                          </CwsHeaderMegamenu>
                        ) : null}
                      </CwsHeaderMenuNavItem>
                    )
                  })}
                </CwsHeaderMenuNav>
              )}
              {isGloballyPricelist() && !Boolean(categories.length) && Boolean(catalogs.length) && (
                <CwsHeaderMenuNav>
                  {catalogs.map((ferry) => (
                    <CwsHeaderMenuNavItem
                      key={ferry.id}
                      href={`/${locale}/${ferry.id}`}
                      icon="ship"
                      label={ferry.title}
                    >
                      <CwsA href={`/${locale}/${ferry.id}`} />
                    </CwsHeaderMenuNavItem>
                  ))}
                </CwsHeaderMenuNav>
              )}

              <CwsHeaderNav>
                <UpperNavigation isMobile />
                <CwsHeaderNavItem>
                  <CwsA target="_blank" hideArrow size="small" href={clubOneUrl}>
                    Club One
                  </CwsA>
                </CwsHeaderNavItem>
                <CwsHeaderLocaleButton language={locale?.toUpperCase()} />
              </CwsHeaderNav>
              <LanguageSelector
                selectedLanguageCode={locale ?? 'en'}
                onSelectLanguage={(locale: Locale) => push({ pathname, query }, asPath, { locale })}
              />
            </HeaderMenuMobile>
          </CwsSection>
        </CwsHeader>
      </CwsHeaderWrapper>
    </>
  )
}
